<template>
  <div class="admin-page">
    <!-- TOPBAR -->
    <header class="admin-topbar">
      <div class="topbar-left">
        <div class="brand">
          <img :src="`${publicPath}logo.png`" alt="INVOXIS" class="brand-logo" />
        </div>
        <router-link to="/" class="breadcrumb">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          Back to Hub
        </router-link>
      </div>
      <div class="topbar-right">
        <div class="user-chip">
          <div class="user-avatar">{{ (username && username.charAt(0).toUpperCase()) || 'A' }}</div>
          <span class="user-name">{{ username }}</span>
        </div>
        <button @click="logout" class="btn-logout">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          Logout
        </button>
      </div>
    </header>

    <!-- MAIN CONTENT -->
    <main class="admin-content">
      <!-- HERO -->
      <div class="hero">
        <div class="hero-eyebrow">
          <span class="hero-eyebrow-dot"></span>
          Administration
        </div>
        <h1 class="hero-title">Platform administration</h1>
        <p class="hero-sub">Manage users, PMS configurations and platform access from a single place.</p>
      </div>

      <!-- STATS -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ stats.totalUsers }}</span>
            <span class="stat-label">Total Users</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon stat-icon-green">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></svg>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ stats.activeUsers }}</span>
            <span class="stat-label">Active Users</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon stat-icon-amber">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ stats.pendingUsers }}</span>
            <span class="stat-label">Pending Approval</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ stats.totalPmsConfigs }}</span>
            <span class="stat-label">PMS Configured</span>
          </div>
        </div>
      </div>

      <!-- Users Pending Approval -->
      <section class="admin-section">
        <div class="section-header">
          <div>
            <h3 class="section-title">Users Pending Approval</h3>
            <p class="section-sub">Review and approve new registrations</p>
          </div>
          <span class="count-badge" v-if="pendingUsers.length > 0">{{ pendingUsers.length }}</span>
        </div>

        <div v-if="pendingUsers.length > 0" class="users-list">
          <div v-for="user in pendingUsers" :key="user.id" class="user-card">
            <div class="user-card-avatar">
              {{ getInitials(user.username || user.email) }}
            </div>
            <div class="user-info">
              <h4 class="user-card-name">{{ user.username }}</h4>
              <p class="user-email">{{ user.email }}</p>
              <p class="user-date">Registered {{ formatDate(user.created_at) }}</p>
            </div>
            <div class="user-actions">
              <button
                @click="approveUser(user.id)"
                class="btn-approve"
                :disabled="processingUser === user.id"
              >
                <span v-if="processingUser === user.id" class="loading-spinner"></span>
                <template v-else>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Approve
                </template>
              </button>
              <button
                @click="rejectUser(user.id)"
                class="btn-reject"
                :disabled="processingUser === user.id"
              >
                Reject
              </button>
            </div>
          </div>
        </div>

        <div v-else class="empty-state">
          <div class="empty-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          </div>
          <h4>All caught up</h4>
          <p>No users are waiting for approval right now.</p>
        </div>
      </section>

      <!-- All Users -->
      <section class="admin-section">
        <div class="section-header">
          <div>
            <h3 class="section-title">All Users</h3>
            <p class="section-sub">{{ allUsers.length }} {{ allUsers.length === 1 ? 'user registered' : 'users registered' }}</p>
          </div>
        </div>

        <div v-if="allUsers.length > 0" class="table-container">
          <table class="users-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Status</th>
                <th>Role</th>
                <th>Registered</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in allUsers" :key="user.id">
                <td class="user-name-cell">{{ user.username }}</td>
                <td class="user-email-cell">{{ user.email }}</td>
                <td>
                  <span class="status-badge" :class="user.status">
                    <span class="status-dot"></span>
                    {{ formatStatus(user.status) }}
                  </span>
                </td>
                <td>
                  <span v-if="user.is_super_admin" class="role-badge admin">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    Admin
                  </span>
                  <span v-else class="role-badge user">User</span>
                </td>
                <td class="date-cell">{{ formatDate(user.created_at) }}</td>
                <td>
                  <button
                    v-if="!user.is_super_admin"
                    @click="toggleUserApproval(user.id, user.status)"
                    class="action-btn"
                    :class="(user.status === 'approved' || user.status === 'active') ? 'disapprove-btn' : 'approve-btn'"
                    :disabled="processingUser === user.id"
                  >
                    {{ (user.status === 'approved' || user.status === 'active') ? 'Disapprove' : 'Approve' }}
                  </button>
                  <span v-else class="no-action">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="empty-state">
          <div class="empty-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </div>
          <h4>No users yet</h4>
          <p>Users will appear here once registered.</p>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import SETTINGS from '@/config/settings.js';

export default {
  name: 'AdminView',
  data() {
    return {
      publicPath: process.env.BASE_URL,
      username: '',
      stats: {
        totalUsers: 0,
        activeUsers: 0,
        pendingUsers: 0,
        totalPmsConfigs: 0
      },
      pendingUsers: [],
      allUsers: [],
      processingUser: null
    };
  },

  methods: {
    logout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.$router.push('/login');
    },

    getInitials(name) {
      if (!name) return '?';
      return name.substring(0, 2).toUpperCase();
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatStatus(status) {
      const statuses = {
        'pending': 'Pending',
        'active': 'Active',
        'approved': 'Approved',
        'inactive': 'Inactive',
        'blocked': 'Blocked'
      };
      return statuses[status] || status;
    },

    getApiUrl(endpoint) {
      return `${SETTINGS.API.baseUrl}${endpoint}`;
    },

    async fetchUserProfile() {
      const token = localStorage.getItem('token');
      if (!token) {
        this.$router.push('/login');
        return;
      }

      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        this.username = user.username || user.email || 'Admin';

        // Check if user is super admin
        if (!user.is_super_admin) {
          this.$router.push('/');
          return;
        }
      } catch (error) {
        console.error('Error loading user:', error);
        this.$router.push('/');
      }
    },

    async fetchPendingUsers() {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const apiUrl = this.getApiUrl('/api/users/pending/');
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.pendingUsers = data || [];
          this.stats.pendingUsers = this.pendingUsers.length;
        }
      } catch (error) {
        console.error('Error fetching pending users:', error);
      }
    },

    async fetchAllUsers() {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        // This endpoint may need to be created in the backend
        const apiUrl = this.getApiUrl('/api/users/');
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.allUsers = data.results || data || [];
          this.stats.totalUsers = this.allUsers.length;
          this.stats.activeUsers = this.allUsers.filter(u => u.status === 'active').length;
        }
      } catch (error) {
        console.error('Error fetching all users:', error);
      }
    },

    async fetchStats() {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Fetch PMS configs count
      try {
        const apiUrl = this.getApiUrl('/api/users/pms-configs/');
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.stats.totalPmsConfigs = (data.results || data || []).length;
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    },

    async approveUser(userId) {
      this.processingUser = userId;
      const token = localStorage.getItem('token');

      try {
        const apiUrl = this.getApiUrl(`/api/users/approve/${userId}/`);
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          // Remove user from pending list
          this.pendingUsers = this.pendingUsers.filter(u => u.id !== userId);
          this.stats.pendingUsers = this.pendingUsers.length;

          // Refresh all users and update stats
          await this.fetchAllUsers();
          this.updateStats();
        } else {
          const data = await response.json();
          alert(data.message || 'Failed to approve user');
        }
      } catch (error) {
        console.error('Error approving user:', error);
        alert('Failed to approve user. Please try again.');
      } finally {
        this.processingUser = null;
      }
    },

    updateStats() {
      this.stats.totalUsers = this.allUsers.length;
      this.stats.activeUsers = this.allUsers.filter(u => u.status === 'active' || u.status === 'approved').length;
      this.stats.pendingUsers = this.pendingUsers.length;
    },

    async toggleUserApproval(userId, currentStatus) {
      const isApproved = currentStatus === 'approved' || currentStatus === 'active';
      const action = isApproved ? 'disapprove' : 'approve';

      if (!confirm(`Are you sure you want to ${action} this user?`)) {
        return;
      }

      this.processingUser = userId;
      const token = localStorage.getItem('token');

      try {
        const endpoint = isApproved ? 'disapprove' : 'approve';
        const apiUrl = this.getApiUrl(`/api/users/${endpoint}/${userId}/`);
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          // Update user in list
          const userIndex = this.allUsers.findIndex(u => u.id === userId);
          if (userIndex !== -1) {
            this.allUsers[userIndex].status = isApproved ? 'pending' : 'approved';
          }
          this.updateStats();
        } else {
          const data = await response.json();
          alert(data.message || `Failed to ${action} user`);
        }
      } catch (error) {
        console.error(`Error ${action}ing user:`, error);
        alert(`Failed to ${action} user. Please try again.`);
      } finally {
        this.processingUser = null;
      }
    },

    async rejectUser(userId) {
      if (!confirm('Are you sure you want to reject this user? This action cannot be undone.')) {
        return;
      }

      this.processingUser = userId;
      const token = localStorage.getItem('token');

      try {
        // This endpoint may need to be created in the backend
        const apiUrl = this.getApiUrl(`/api/users/reject/${userId}/`);
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          // Remove user from pending list
          this.pendingUsers = this.pendingUsers.filter(u => u.id !== userId);
          this.stats.pendingUsers = this.pendingUsers.length;
        } else {
          const data = await response.json();
          alert(data.message || 'Failed to reject user');
        }
      } catch (error) {
        console.error('Error rejecting user:', error);
        alert('Failed to reject user. Please try again.');
      } finally {
        this.processingUser = null;
      }
    }
  },

  async mounted() {
    await this.fetchUserProfile();
    await Promise.all([
      this.fetchPendingUsers(),
      this.fetchAllUsers(),
      this.fetchStats()
    ]);
  }
};
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.admin-page {
  min-height: 100vh;
  background: #f5f3ff;
  font-family: 'DM Sans', sans-serif;
  color: #12113a;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  position: relative;
  overflow-x: hidden;
}

/* Aurora background */
.admin-page::before,
.admin-page::after {
  content: '';
  position: fixed;
  border-radius: 50%;
  filter: blur(120px);
  z-index: 0;
  pointer-events: none;
}

.admin-page::before {
  width: 620px;
  height: 620px;
  top: -180px;
  right: -160px;
  background: radial-gradient(circle, rgba(128,117,240,0.35) 0%, rgba(128,117,240,0) 70%);
  animation: floatSlow 18s ease-in-out infinite;
}

.admin-page::after {
  width: 540px;
  height: 540px;
  bottom: -200px;
  left: -160px;
  background: radial-gradient(circle, rgba(123,82,200,0.28) 0%, rgba(123,82,200,0) 70%);
  animation: floatSlow 22s ease-in-out infinite reverse;
}

@keyframes floatSlow {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(30px, -20px) scale(1.05); }
}

.admin-topbar,
.admin-content {
  position: relative;
  z-index: 1;
}

/* ── TOPBAR ── */
.admin-topbar {
  background: linear-gradient(180deg, #5b58d4 0%, #4e4bbb 30%, #6b4fd8 70%, #7b52c8 100%);
  padding: 0 40px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 50;
}

.admin-topbar::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  pointer-events: none;
  background: linear-gradient(90deg,
    rgba(255,255,255,0) 0%,
    rgba(255,255,255,0.15) 15%,
    rgba(255,255,255,0.6) 50%,
    rgba(255,255,255,0.15) 85%,
    rgba(255,255,255,0) 100%);
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.brand {
  display: flex;
  align-items: center;
}

.brand-logo {
  height: 17px;
  width: auto;
  object-fit: contain;
  display: block;
  filter: brightness(0) invert(1);
}

.breadcrumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 100px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.18);
  color: rgba(255,255,255,0.85);
  text-decoration: none;
  font-size: 12px;
  font-weight: 600;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.breadcrumb:hover {
  background: rgba(255,255,255,0.16);
  border-color: rgba(255,255,255,0.28);
  color: white;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-chip {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 14px 5px 5px;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 100px;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255,255,255,0.22);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  font-family: 'Bricolage Grotesque', sans-serif;
  letter-spacing: -0.3px;
}

.user-name {
  font-size: 13px;
  font-weight: 600;
  color: white;
}

.btn-logout {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #ef4444;
  border: 1px solid #ef4444;
  border-radius: 8px;
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  font-family: inherit;
}

.btn-logout:hover {
  background: #dc2626;
  border-color: #dc2626;
  box-shadow: 0 4px 12px rgba(239,68,68,0.35);
}

/* ── MAIN ── */
.admin-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 32px 64px;
  animation: fadeIn 0.4s 0.05s both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* ── HERO ── */
.hero {
  margin-bottom: 32px;
  animation: heroIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 8px;
  background: rgba(255,255,255,0.7);
  border: 1px solid rgba(128,117,240,0.25);
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  color: #5b58d4;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 16px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 2px 12px rgba(91,88,212,0.08);
}

.hero-eyebrow-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #5b58d4;
  box-shadow: 0 0 0 3px rgba(91,88,212,0.18);
}

.hero-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 34px;
  font-weight: 800;
  color: #12113a;
  letter-spacing: -0.9px;
  line-height: 1.1;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #12113a 0%, #5b58d4 55%, #7b52c8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-sub {
  font-size: 16px;
  color: #6b6890;
  font-weight: 400;
  max-width: 560px;
  line-height: 1.5;
}

@keyframes heroIn {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ── STATS ── */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
  margin-bottom: 32px;
  animation: heroIn 0.6s 0.1s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  background: rgba(255,255,255,0.72);
  border: 1px solid rgba(235,233,248,0.9);
  border-radius: 14px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  box-shadow: 0 2px 8px rgba(15,14,46,0.04), inset 0 1px 0 rgba(255,255,255,0.7);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}

.stat-card:hover {
  border-color: rgba(128,117,240,0.35);
  box-shadow: 0 8px 24px rgba(91,88,212,0.12), inset 0 1px 0 rgba(255,255,255,0.7);
  transform: translateY(-2px);
}

.stat-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(128,117,240,0.12);
  color: #5b58d4;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon-green {
  background: rgba(0,184,148,0.12);
  color: #059669;
}

.stat-icon-amber {
  background: rgba(245,158,11,0.14);
  color: #d97706;
}

.stat-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-value {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 26px;
  font-weight: 800;
  color: #12113a;
  line-height: 1;
  letter-spacing: -0.6px;
}

.stat-label {
  font-size: 12px;
  color: #9896b8;
  font-weight: 500;
}

/* ── ADMIN SECTION ── */
.admin-section {
  background: rgba(255,255,255,0.75);
  border: 1px solid rgba(235,233,248,0.9);
  border-radius: 18px;
  overflow: hidden;
  box-shadow:
    0 1px 3px rgba(15,14,46,0.04),
    0 12px 40px rgba(91,88,212,0.08),
    inset 0 1px 0 rgba(255,255,255,0.7);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  margin-bottom: 24px;
  animation: heroIn 0.6s 0.2s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(235,233,248,0.7);
  gap: 14px;
  background: linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 100%);
}

.section-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #12113a;
  letter-spacing: -0.3px;
  line-height: 1.2;
}

.section-sub {
  font-size: 12px;
  color: #9896b8;
  margin-top: 3px;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 26px;
  padding: 0 10px;
  background: linear-gradient(180deg, #5b58d4 0%, #4e4bbb 100%);
  color: white;
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 12px;
  font-weight: 700;
  border-radius: 100px;
  letter-spacing: -0.2px;
}

/* ── USERS LIST (Pending) ── */
.users-list {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.user-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: rgba(255,255,255,0.5);
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.user-card:hover {
  border-color: rgba(128,117,240,0.35);
  background: white;
  box-shadow: 0 8px 20px rgba(91,88,212,0.1);
  transform: translateY(-1px);
}

.user-card-avatar {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #5b58d4 0%, #7b52c8 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-family: 'Bricolage Grotesque', sans-serif;
  font-weight: 700;
  font-size: 15px;
  letter-spacing: -0.3px;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(91,88,212,0.25);
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-card-name {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #12113a;
  letter-spacing: -0.2px;
  margin-bottom: 3px;
  line-height: 1.2;
}

.user-email {
  font-size: 13px;
  color: #4a4870;
  margin-bottom: 3px;
}

.user-date {
  font-size: 11px;
  color: #9896b8;
  font-weight: 500;
}

.user-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.btn-approve {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(180deg, #10b981 0%, #059669 100%);
  color: white;
  border: 1px solid #059669;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  font-family: inherit;
  min-width: 96px;
}

.btn-approve:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16,185,129,0.35);
}

.btn-approve:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-reject {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  background: white;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  font-family: inherit;
}

.btn-reject:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #fca5a5;
}

.btn-reject:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-spinner {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255,255,255,0.35);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── EMPTY STATE ── */
.empty-state {
  padding: 64px 24px;
  text-align: center;
}

.empty-icon {
  width: 74px;
  height: 74px;
  border-radius: 50%;
  background: linear-gradient(135deg, #5b58d4 0%, #7b52c8 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  box-shadow:
    0 12px 32px rgba(91,88,212,0.28),
    0 0 0 8px rgba(128,117,240,0.08),
    0 0 0 16px rgba(128,117,240,0.04);
  animation: pulseHalo 3s ease-in-out infinite;
}

@keyframes pulseHalo {
  0%, 100% {
    box-shadow:
      0 12px 32px rgba(91,88,212,0.28),
      0 0 0 8px rgba(128,117,240,0.08),
      0 0 0 16px rgba(128,117,240,0.04);
  }
  50% {
    box-shadow:
      0 12px 32px rgba(91,88,212,0.35),
      0 0 0 12px rgba(128,117,240,0.1),
      0 0 0 24px rgba(128,117,240,0.05);
  }
}

.empty-state h4 {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: #12113a;
  letter-spacing: -0.3px;
  margin-bottom: 6px;
}

.empty-state p {
  font-size: 13px;
  color: #9896b8;
  margin: 0 auto;
  max-width: 340px;
  line-height: 1.55;
}

/* ── USERS TABLE ── */
.table-container {
  overflow-x: auto;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
}

.users-table th {
  padding: 12px 20px;
  text-align: left;
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  color: #9896b8;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 1px solid rgba(235,233,248,0.7);
  background: rgba(245,243,255,0.4);
}

.users-table td {
  padding: 14px 20px;
  border-bottom: 1px solid rgba(235,233,248,0.5);
  color: #4a4870;
  font-size: 13px;
  vertical-align: middle;
}

.users-table tbody tr:last-child td {
  border-bottom: none;
}

.users-table tbody tr {
  transition: background 0.15s ease;
}

.users-table tbody tr:hover {
  background: rgba(128,117,240,0.04);
}

.user-name-cell {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-weight: 700;
  color: #12113a;
  font-size: 13px;
  letter-spacing: -0.2px;
}

.user-email-cell {
  color: #6b6890;
}

.date-cell {
  color: #9896b8;
  font-size: 12px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 9px;
  border-radius: 100px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border: 1px solid transparent;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

.status-badge.active,
.status-badge.approved {
  background: #ecfdf5;
  color: #059669;
  border-color: #a7f3d0;
}

.status-badge.pending {
  background: #fffbeb;
  color: #d97706;
  border-color: #fde68a;
}

.status-badge.inactive,
.status-badge.blocked {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecaca;
}

/* Action buttons in table */
.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  border: 1px solid transparent;
  font-family: inherit;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-btn.approve-btn {
  background: #ecfdf5;
  color: #059669;
  border-color: #a7f3d0;
}

.action-btn.approve-btn:hover:not(:disabled) {
  background: #059669;
  color: white;
  border-color: #059669;
}

.action-btn.disapprove-btn {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecaca;
}

.action-btn.disapprove-btn:hover:not(:disabled) {
  background: #dc2626;
  color: white;
  border-color: #dc2626;
}

.no-action {
  color: #cfcce8;
  font-weight: 500;
}

.role-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 100px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border: 1px solid transparent;
}

.role-badge.admin {
  background: linear-gradient(180deg, #5b58d4 0%, #4e4bbb 100%);
  color: white;
  border-color: #4e4bbb;
}

.role-badge.user {
  background: rgba(128,117,240,0.08);
  color: #6b6890;
  border-color: rgba(128,117,240,0.2);
}

/* ── RESPONSIVE ── */
@media (max-width: 768px) {
  .admin-topbar {
    padding: 12px 20px;
    height: auto;
    flex-direction: column;
    gap: 12px;
  }

  .topbar-left {
    width: 100%;
    justify-content: space-between;
    gap: 12px;
  }

  .topbar-right {
    width: 100%;
    justify-content: space-between;
  }

  .admin-content {
    padding: 32px 16px 48px;
  }

  .hero-title {
    font-size: 26px;
  }

  .stats-grid {
    grid-template-columns: 1fr 1fr;
  }

  .user-card {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
    gap: 12px;
  }

  .user-card-avatar {
    margin: 0 auto;
  }

  .user-actions {
    justify-content: center;
    width: 100%;
  }

  .btn-approve,
  .btn-reject {
    flex: 1;
  }

  .users-table {
    font-size: 12px;
  }

  .users-table th,
  .users-table td {
    padding: 10px 12px;
  }
}
</style>
